import React from 'react';
import { Bubble } from 'tea-component';
import GameImage from '@/pages/home/components/GameImage';
import { getIntelligenceGamePath } from '@/pages/home/utils/utils';
import { UPCOMING_GAMES, NEWRELEASEGAMES, NOTEWORTHYGAMES, BIGRISER } from './constant';
import { timeFormat } from './common';
import CountryRankCell from '../components/CountryRankCell';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { DataSourceIcon, METRIC_NAME } from '@/pages/home/pages/home/components/DataSource';
import { getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';

/** 列配置类型 */
export interface SpotlightColumnConfig {
  key: string;
  title: string;
  icon?: string | string[];  // 支持单个或多个 icon
  width?: number;
  // tbody内的列宽
  tbodyColWidth?: number;
  align?: 'left' | 'right' | 'center';
  tooltipContent?: React.ReactNode;  // 表头悬浮窗内容
  learnMoreUrl?: string;             // 表头"了解更多"跳转链接
  render: (item: any, entityType: string, cardName?: string) => React.ReactNode;
}

/** 卡片配置类型（列 + 空态提示） */
export interface SpotlightCardConfig {
  columns: SpotlightColumnConfig[];
  nodataMsg?: string;
  cardTitle?: string;
}

/** 格式化数字：大数缩写 */
const formatNumber = (val: any): string => {
  if (val == null || val === '' || val === 0) return '-';
  const num = Number(val);
  if (isNaN(num)) return '-';
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toFixed(0);
};

/** 格式化增长率百分比（后端返回小数，如 0.46 表示 46%） */
const formatRatio = (ratio: any): string => {
  if (ratio == null || ratio === 0) return '-';
  const num = Number(ratio);
  if (isNaN(num)) return '-';
  const pct = num * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

/** 渲染指标单元格：值 + 增长率 */
const renderMetricCell = (value: any, growth?: any, growthType?: string) => {
  const displayVal = typeof value === 'number' ? formatNumber(value) : (value ?? '-');
  const growthText = growth == null || growth === '' ? '-' : `${growth}`;
  const isGrowthEmpty = growthText === '-';

  let gType = growthType === 'up' || growthType === 'down' ? growthType : '';
  if (!gType && !isGrowthEmpty) {
    const growthNum = Number(growthText.replace(/[%+,]/g, ''));
    if (!isNaN(growthNum) && growthNum !== 0) {
      gType = growthNum > 0 ? 'up' : 'down';
    }
  }

  return (
    <div className="dbh-game-spotlight-card__metric-cell">
      <div className="dbh-game-spotlight-card__metric-value">{displayVal}</div>
      <span className={`dbh-game-spotlight-card__growth dbh-game-spotlight-card__growth--${gType} ${isGrowthEmpty ? 'dbh-game-spotlight-card__growth--empty' : ''}`}>
        {growthText}
        {!isGrowthEmpty && (gType === 'up' || gType === 'down') && (
          <SvgIcon
            className={`dbh-game-spotlight-card__growth-icon dbh-game-spotlight-card__growth-icon--${gType}`}
            type={gType === 'up' ? 'green-up-new' : 'red-down-new'}
          />
        )}
      </span>
    </div>
  );
};

/** 游戏下钻 tab：Big Riser / 手游即将上线 → storeRank，其余 → overview */
const getGameDrillTab = (entityType: string, cardName?: string): string => {
  if (cardName === BIGRISER) {
    return 'storeRank';
  }
  if (cardName === UPCOMING_GAMES && entityType === 'mobile') {
    return 'storeRank';
  }
  return 'overview';
};

/** Game 列 */
const gameColumn = (formatMessage: any): SpotlightColumnConfig => ({
  key: 'game',
  title: formatMessage({ id: 'dbh_metric_game' }),
  align: 'left',
  render: (item: any, entityType: string, cardName?: string) => {
    const gameName = item?.name || item?.entity_name || '';
    const publisher = item?.publisher || '';
    const cover = item?.cover;
    const gameId = item?.unified_id || item?.edition_id || item?.app_id || item?.id;
    const tab = getGameDrillTab(entityType, cardName);
    const href = `/v2${getIntelligenceGamePath(gameId, entityType, tab)}`;
    return (
      <a className="dbh-game-spotlight-card__game" href={href} target="_blank" rel="noreferrer">
        <div className="dbh-game-spotlight-card__cover">
          <GameImage src={cover} style={{ width: 36, height: 36, borderRadius: 8 }} />
        </div>
        <div className="dbh-game-spotlight-card__info">
            <div className="dbh-game-spotlight-card__name">
              <Bubble dark arrowPointAtCenter content={gameName}>
              {gameName}
              </Bubble>
            </div>
          <div className="dbh-game-spotlight-card__publisher" title={publisher}>{publisher}</div>
        </div>
      </a>
    );
  },
});

/** 悬浮窗内 icon + 文案组合，保证垂直居中对齐 */
const renderTooltipIconText = (iconType: string, text: React.ReactNode) => (
  <span className="dbh-game-spotlight-tooltip__inline">
    <DataSourceIcon className="dbh-game-spotlight-tooltip__icon" type={iconType} />
    <span className="dbh-game-spotlight-tooltip__inline-text">{text}</span>
  </span>
);

// ====================================================================
//  公共 tooltip 构造函数（减少重复）
// ====================================================================

/** sensortower DAU tooltip（mobile Noteworthy / Big Risers 共用） */
const makeSensortowerDauTip = (formatMessage: any, extraLines?: React.ReactNode) => (
  <span>
    {extraLines}
    {formatMessage({ id: 'dbh_metric_dataof_comesfrom' }, { sourceName: formatMessage({ id: 'dbh_metric_dau' }) })}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="sensortowernew" />
    {METRIC_NAME.sensortower}
  </span>
);

/** sensortower Revenue tooltip（mobile Noteworthy / Big Risers 共用） */
const makeSensortowerRevenueTip = (formatMessage: any, extraLines?: React.ReactNode) => (
  <span>
    {extraLines}
    {formatMessage({ id: 'dbh_metric_dataof_comesfrom' }, { sourceName: formatMessage({ id: 'dbh_metric_revenue_s' }) })}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="sensortowernew" />
    {METRIC_NAME.sensortower}
  </span>
);

/** PC PCU tooltip（pc Noteworthy / New Release / Big Risers 共용） */
const makePcuTip = (formatMessage: any, extraLines?: React.ReactNode) => (
  <span>
    {extraLines}
    {formatMessage(
      { id: 'dbh_metric_dataof_comesfrom' },
      { sourceName: <><DataSourceIcon className="datasource-comp-sourcessicon" type="steamicon" />PCU</> },
    )}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="steamicon" />
    {METRIC_NAME.steam}
  </span>
);

/** PC Units tooltip（pc Noteworthy / New Release / Big Risers 共用） */
const makePcUnitsTip = (formatMessage: any) => (
  <span>
    {formatMessage({ id: 'dbh_spotlight_tip_sum_units' })}<br />
    {formatMessage(
      { id: 'dbh_metric_dataof_comesfrom' },
      {
        sourceName: getLocale() === LANGUAGE_CN ? (
          <><DataSourceIcon className="datasource-comp-sourcessicon" type="steamicon" />{formatMessage({ id: 'dbh_spotlight_units' })}</>
        ) : (
          <><DataSourceIcon className="datasource-comp-sourcessicon" type="steamicon" />Steam Sales {formatMessage({ id: 'dbh_spotlight_units' })}</>
        ),
      },
    )}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="alinea" />
    {METRIC_NAME.alinea}
  </span>
);

/** Console DAU tooltip（console Noteworthy / Big Risers 共用） */
const makeConsoleDauTip = (formatMessage: any, extraLines?: React.ReactNode) => (
  <span>
    {extraLines}
    {formatMessage({ id: 'dbh_spotlight_tip_console_dau_def' })}<br />
    {formatMessage({ id: 'dbh_spotlight_tip_console_dau_granularity' })}<br />
    {formatMessage({ id: 'dbh_spotlight_tip_console_dau_update' })}<br />
    {formatMessage({ id: 'dbh_spotlight_tip_console_dau_agg' })}<br />
    {formatMessage({ id: 'dbh_spotlight_tip_console_dau_source' })}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="amperedau" />Other Vendor
  </span>
);

/** Console Revenue tooltip（console Noteworthy / Big Risers 共用） */
const makeConsoleRevenueTip = (formatMessage: any, extraLines?: React.ReactNode) => (
  <span>
    {extraLines}
    {formatMessage(
      { id: 'dbh_metric_dataof_comesfrom' },
      { sourceName: formatMessage({ id: 'dbh_spotlight_tip_digital_revenue' }) },
    )}
    <DataSourceIcon className="datasource-comp-sourcessicon" type="mscience" />
    {METRIC_NAME.mscience}
  </span>
);

// ====================================================================
//  entityType × cardName 配置（12 种组合）
//  每个组合包含 columns（列配置）和 nodataMsg（空态提示）
// ====================================================================

const getConfigMap = (formatMessage: any): Record<string, SpotlightCardConfig> => ({

  // ======================== Mobile ========================

  [`mobile-${NOTEWORTHYGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_no_data' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_noteworthy_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'dau',
        title: formatMessage({ id: 'dbh_metric_dau' }),
        icon: 'sensortowernew',
        width: 68,
        align: 'right',
        tooltipContent: makeSensortowerDauTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_avg' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_mobile_range' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=mobile&metric=DAU&pageid=sensortower',
        render: (item: any) => renderMetricCell(item?.sensortower__dau, formatRatio(item?.sensortower__dau_ratio)),
      },
      {
        key: 'revenue',
        title: formatMessage({ id: 'dbh_metric_revenue_s' }),
        icon: 'sensortowernew',
        width: 104,
        align: 'right',
        tooltipContent: makeSensortowerRevenueTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_sum' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_sum_revenue_commission' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_mobile_range' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=mobile&metric=Revenue&pageid=sensortower',
        render: (item: any) => renderMetricCell(item?.sensortower__revenue, formatRatio(item?.sensortower__revenue_ratio)),
      },
    ],
  },

  [`mobile-${NEWRELEASEGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_newrelease_mobile_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_newrelease_mobile_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'freetop10',
        title: formatMessage({ id: 'dbh_metric_freetop10' }),
        icon: ['appstore', 'allcountry'],
        width: 125,
        align: 'right',
        tooltipContent: (
          <span>
            {formatMessage(
              { id: 'dbh_spotlight_tip_free_ranking_data_of' },
              {
                freeSource: renderTooltipIconText(
                  'appstore',
                  formatMessage({ id: 'dbh_metric_freetop10' }),
                ),
                appStoreSource: renderTooltipIconText('appstore', 'AppStore'),
              },
            )}<br />
            {formatMessage({ id: 'dbh_spotlight_tip_free_count_countries' })}
          </span>
        ),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=appstore',
        render: (item: any) => (
          <CountryRankCell
            count={item?.market_cnt}
            countries={item?.markets?.map((m: any) => m?.market) || []}
            markets={item?.markets || []}
            chartLabel={formatMessage({ id: 'dbh_metric_freetop10' })}
            chartIcon="appstore"
          />
        ),
      },
    ],
  },

  [`mobile-${UPCOMING_GAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_upcoming_mobile_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_upcoming_mobile_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'pre_registration_top5',
        title: formatMessage({ id: 'dbh_spotlight_pre_registration_top5' }),
        icon: ['googleplay', 'allcountry'],
        width: getLocale() === LANGUAGE_CN ? 125 : 210,
        tbodyColWidth: 125,
        align: 'right',
        tooltipContent: (
          <span>
            {formatMessage(
              { id: 'dbh_spotlight_tip_pre_registration_ranking_data_of' },
              {
                chartSource: renderTooltipIconText(
                  'googleplay',
                  formatMessage({ id: 'dbh_spotlight_pre_registration_top5' }),
                ),
                googlePlaySource: renderTooltipIconText(
                  'googleplay',
                  METRIC_NAME.googleplay,
                ),
              },
            )}
            {formatMessage({ id: 'dbh_spotlight_tip_pre_registration_count_countries' })}
          </span>
        ),
        learnMoreUrl:
          '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=googleplay',
        render: (item: any) => (
          <CountryRankCell
            count={item?.market_cnt}
            countries={item?.markets?.map((m: any) => m?.market) || []}
            markets={item?.markets || []}
            chartLabel={formatMessage({ id: 'dbh_spotlight_pre_registration_top5' })}
            chartIcon="googleplay"
          />
        ),
      },
      {
        key: 'release',
        title: formatMessage({ id: 'dbh_metric_release' }),
        width: 104,
        align: 'right',
        render: (item: any) => <div className="dbh-game-spotlight-card__metric-value">{timeFormat(item?.release_time) || '-'}</div>,
      },
    ],
  },

  [`mobile-${BIGRISER}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_bigriser_mobile_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_big_risers' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'dau',
        title: formatMessage({ id: 'dbh_metric_avg_dau' }),
        icon: 'sensortowernew',
        width: 90,
        align: 'right',
        tooltipContent: makeSensortowerDauTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_bigriser_dau' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_avg' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_mobile_range' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=mobile&metric=DAU&pageid=sensortower',
        render: (item: any) => renderMetricCell(item?.sensortower__dau, formatRatio(item?.sensortower__dau_ratio)),
      },
      {
        key: 'revenue',
        title: formatMessage({ id: 'dbh_metric_sum_iap_rev' }),
        icon: 'sensortowernew',
        width: 116,
        align: 'right',
        tooltipContent: makeSensortowerRevenueTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_sum' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_revenue_commission' })}<br />
          {formatMessage({ id: 'dbh_spotlight_tip_mobile_range' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=mobile&metric=DAU&pageid=sensortower',
        render: (item: any) => renderMetricCell(item?.sensortower__revenue, formatRatio(item?.sensortower__revenue_ratio)),
      },
    ],
  },

  // ======================== PC ========================

  [`pc-${NOTEWORTHYGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_no_data' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_noteworthy_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'pcu',
        title: formatMessage({ id: 'dbh_metric_pcu' }),
        icon: 'steamblack',
        width: 110,
        align: 'right',
        tooltipContent: makePcuTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=steam',
        render: (item: any) => renderMetricCell(item?.steamcharts__pcu, formatRatio(item?.steamcharts__pcu_ratio)),
      },
      {
        key: 'units',
        title: formatMessage({ id: 'dbh_spotlight_units' }),
        icon: 'steamblack',
        width: 90,
        align: 'right',
        tooltipContent: makePcUnitsTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/sources/IntelligenceSources?sourceValue=Alinea',
        render: (item: any) => renderMetricCell(item?.mscience__units, formatRatio(item?.mscience__units_ratio)),
      },
    ],
  },

  [`pc-${NEWRELEASEGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_no_data' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_newrelease_pc_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'pcu',
        title: formatMessage({ id: 'dbh_metric_pcu' }),
        icon: 'steamblack',
        width: 110,
        align: 'right',
        tooltipContent: makePcuTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=steam',
        render: (item: any) => renderMetricCell(item?.steamcharts__pcu, formatRatio(item?.steamcharts__pcu_ratio)),
      },
      {
        key: 'units',
        title: formatMessage({ id: 'dbh_spotlight_units' }),
        icon: 'steamblack',
        width: 90,
        align: 'right',
        tooltipContent: makePcUnitsTip(formatMessage),
        learnMoreUrl: '/v2/intelligence/sourcesMetrics/sources/IntelligenceSources?sourceValue=Alinea',
        render: (item: any) => renderMetricCell(item?.mscience__units, formatRatio(item?.mscience__units_ratio)),
      },
    ],
  },

  [`pc-${UPCOMING_GAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_no_data' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_upcoming_pc_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'wishlist',
        title: formatMessage({ id: 'dbh_spotlight_wishlist' }),
        icon: 'steamblack',
        width: 90,
        align: 'right',
        tooltipContent: (
          <span>
            {formatMessage(
              { id: 'dbh_spotlight_tip_ranking_data_of' },
              {
                source: (
                  <>
                    <DataSourceIcon
                      className="datasource-comp-sourcessicon"
                      type="steamicon"
                    />
                    {getLocale() === LANGUAGE_CN
                      ? `${METRIC_NAME.steam}${formatMessage({
                        id: 'dbh_spotlight_wishlist',
                      })}`
                      : `${METRIC_NAME.steam} ${formatMessage({
                        id: 'dbh_spotlight_wishlist',
                      })}`}
                  </>
                ),
              },
            )}
            <DataSourceIcon
              className="datasource-comp-sourcessicon"
              type="steamicon"
            />
            {METRIC_NAME.steam}
            .
          </span>
        ),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=steam',
        render: (item: any) => renderMetricCell(
          item?.rank != null ? `#${item.rank}` : null,
          item?.rank_change != null && item?.rank_change !== 0 ? `${Math.abs(item.rank_change)}` : item?.rank_change === 0 ? '=' : undefined,
          item?.rank_change > 0 ? 'up' : item?.rank_change < 0 ? 'down' : '',
        ),
      },
      {
        key: 'release',
        title: formatMessage({ id: 'dbh_metric_release' }),
        width: 90,
        align: 'right',
        render: (item: any) => <div className="dbh-game-spotlight-card__metric-value">{timeFormat(item?.release_time) || '-'}</div>,
      },
    ],
  },

  [`pc-${BIGRISER}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_bigriser_mobile_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_big_risers' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'pcu',
        title: formatMessage({ id: 'dbh_metric_avg_pcu' }),
        icon: 'steamblack',
        width: 95,
        align: 'right',
        tooltipContent: makePcuTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_bigriser_pcu' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=steam',
        render: (item: any) => renderMetricCell(item?.steamcharts__pcu, formatRatio(item?.steamcharts__pcu_ratio)),
      },
      {
        key: 'units',
        title: formatMessage({ id: 'dbh_metric_sum_units' }),
        icon: 'steamblack',
        width: 98,
        align: 'right',
        tooltipContent: makePcUnitsTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/sources/IntelligenceSources?sourceValue=Alinea',
        render: (item: any) => renderMetricCell(item?.mscience__units, formatRatio(item?.mscience__units_ratio)),
      },
    ],
  },

  // ======================== Console ========================

  [`console-${NOTEWORTHYGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_no_data' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_noteworthy_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'dau',
        title: formatMessage({ id: 'dbh_metric_dau' }),
        icon: 'amperedau',
        width: 68,
        align: 'right',
        tooltipContent: makeConsoleDauTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=combine&metric=ampere_dau&pageid=Other%20Vendor',
        render: (item: any) => renderMetricCell(item?.ampere__ampere_dau, formatRatio(item?.ampere__ampere_dau_ratio)),
      },
      {
        key: 'revenue',
        title: formatMessage({ id: 'dbh_metric_revenue_s' }),
        icon: 'mscience',
        width: 105,
        align: 'right',
        tooltipContent: makeConsoleRevenueTip(formatMessage),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=console&metric=digitalRevenue&pageid=mscience',
        render: (item: any) => renderMetricCell(item?.mscience__digitalRevenue, formatRatio(item?.mscience__digitalRevenue_ratio)),
      },
    ],
  },

  [`console-${NEWRELEASEGAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_newrelease_console_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_newrelease_console_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'newrelease_top10',
        title: formatMessage({ id: 'dbh_spotlight_newrelease_top10' }),
        icon: ['playstation', 'allcountry'],
        width: 185,
        align: 'right',
        tooltipContent: (
          <span>
            {formatMessage(
              { id: 'dbh_spotlight_tip_newrelease_ranking_data_of' },
              {
                newGamesSource: renderTooltipIconText(
                  'playstation',
                  formatMessage({ id: 'dbh_storerank_newgames' }),
                ),
                playStationSource: renderTooltipIconText(
                  'playstation',
                  formatMessage({ id: 'dbh_spotlight_tip_play_station' }),
                ),
              },
            )}<br />
            {formatMessage({ id: 'dbh_spotlight_tip_newrelease_count_countries' })}
          </span>
        ),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=playstation',
        render: (item: any) => (
          <CountryRankCell
            count={item?.market_cnt}
            countries={item?.markets?.map((m: any) => m?.market) || []}
            markets={item?.markets || []}
            chartLabel={formatMessage({ id: 'dbh_spotlight_newrelease_top10' })}
            chartIcon="playstation"
          />
        ),
      },
    ],
  },

  [`console-${UPCOMING_GAMES}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_upcoming_console_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_upcoming_console_games' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'comingsoon_top10',
        title: formatMessage({ id: 'dbh_spotlight_comingsoon_top10' }),
        icon: ['playstation', 'allcountry'],
        width: 190,
        align: 'right',
        tooltipContent: (
          <span>
            {formatMessage(
              { id: 'dbh_spotlight_tip_comingsoon_ranking_data_of' },
              {
                comingSoonSource: renderTooltipIconText(
                  'playstation',
                  formatMessage({ id: 'dbh_spotlight_comingsoon_top10' }),
                ),
                playStationSource: renderTooltipIconText(
                  'playstation',
                  formatMessage({ id: 'dbh_spotlight_tip_play_station' }),
                ),
              },
            )}<br />
            {formatMessage({ id: 'dbh_spotlight_tip_comingsoon_count_countries' })}
          </span>
        ),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=&metric=&pageid=playstation',
        render: (item: any) => (
          <CountryRankCell
            count={item?.market_cnt}
            countries={item?.markets?.map((m: any) => m?.market) || []}
            markets={item?.markets || []}
            chartLabel={formatMessage({ id: 'dbh_spotlight_comingsoon_top10' })}
            chartIcon="playstation"
          />
        ),
      },
    ],
  },

  [`console-${BIGRISER}`]: {
    nodataMsg: formatMessage({ id: 'dbh_spotlight_bigriser_nodata' }),
    cardTitle: formatMessage({ id: 'dbh_spotlight_big_risers' }),
    columns: [
      gameColumn(formatMessage),
      {
        key: 'dau',
        title: formatMessage({ id: 'dbh_metric_avg_dau' }),
        icon: 'amperedau',
        width: 90,
        align: 'right',
        tooltipContent: makeConsoleDauTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_bigriser_console_dau' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=combine&metric=ampere_dau&pageid=Other%20Vendor',
        render: (item: any) => renderMetricCell(item?.ampere__ampere_dau, formatRatio(item?.ampere__ampere_dau_ratio)),
      },
      {
        key: 'revenue',
        title: formatMessage({ id: 'dbh_metric_sum_revenue_console' }),
        icon: 'mscience',
        width: 122,
        align: 'right',
        tooltipContent: makeConsoleRevenueTip(formatMessage, <>
          {formatMessage({ id: 'dbh_spotlight_tip_sum' })}<br />
        </>),
        learnMoreUrl: '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics?gameType=console&metric=digitalRevenue&pageid=mscience',
        render: (item: any) => renderMetricCell(item?.mscience__digitalRevenue, formatRatio(item?.mscience__digitalRevenue_ratio)),
      },
    ],
  },
});

/**
 * 根据 entityType + cardName 获取卡片配置（列 + 空态提示 + 标题）
 */
export const getSpotlightConfig = (
  entityType: string,
  cardName: string,
  formatMessage: any,
): SpotlightCardConfig => {
  const configMap = getConfigMap(formatMessage);
  const key = `${entityType}-${cardName}`;
  return configMap[key] || configMap[`mobile-${NOTEWORTHYGAMES}`];
};
